import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Tabs, Tab, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaPrint, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getEmployeeByEmail, getEmployeeByUid, getEmployee } from '../services/firebase/employeeService';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import PersonalDetailsSection from '../components/employee/PersonalDetailsSection';
import JobDetailsSection from '../components/employee/JobDetailsSection';
import AttendanceCalendarView from '../components/attendance/calendar/AttendanceCalendarView';
import LeaveBalanceSection from '../components/employee/LeaveBalanceSection';
import DocumentsSection from '../components/employee/DocumentsSection';
import CareerGrowthSection from '../components/employee/CareerGrowthSection';
import '../assets/css/employee.css';

const MyProfilePage = () => {
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);

  // Document preview states
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handlePreview = (url, title) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
    setShowPreview(true);
  };

  useEffect(() => {
    fetchMyProfile();
  }, [user]);

  const fetchMyProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const email = user?.email;
      const uid = user?.uid || user?.id;
      const employeeId = user?.employeeId;

      console.log('[MyProfilePage] Fetching profile for:', { email, uid, employeeId });

      if (!email && !uid) {
        setError('Unable to identify your profile. Please contact HR.');
        setLoading(false);
        return;
      }

      // Try to find employee by email first
      let response = await getEmployeeByEmail(email);

      // If not found by email, try by UID (robust lookup on collection fields)
      if (!response.success || !response.data) {
        console.log('[MyProfilePage] Not found by email, trying UID:', uid);
        response = await getEmployeeByUid(uid);
      }

      // If still not found and we have employeeId, try that (direct document ID lookup)
      if ((!response?.success || !response?.data) && employeeId) {
        console.log('[MyProfilePage] Trying employeeId:', employeeId);
        try {
          response = await getEmployee(employeeId);
        } catch (e) {
          console.log('[MyProfilePage] Not found by employeeId either');
        }
      }

      if (response?.success && response?.data) {
        console.log('[MyProfilePage] Employee Data Loaded:', response.data);
        setEmployee(response.data);
      } else {
        setError('Your employee profile was not found. Please contact HR to set up your profile.');
      }
    } catch (error) {
      console.error('[MyProfilePage] Error fetching profile:', error);
      setError('Failed to load your profile. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');

      const element = document.querySelector('.profile-content');
      if (!element) {
        toast.error('Could not generate PDF');
        return;
      }

      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`My_Profile_${employee?.personal?.fullName || user?.email || 'Employee'}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Active': 'success',
      'Inactive': 'danger',
      'On Leave': 'warning',
      'Probation': 'info'
    };
    return <Badge bg={variants[status] || 'secondary'} className="fs-6">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
        <Sidebar />
        <div className="flex-grow-1 profile-main-content">
          <Navbar />
          <Container fluid className="py-3 py-md-4 px-2 px-md-4">
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading your profile...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  // If no employee record found, create a basic profile from user data
  const displayEmployee = employee || {
    id: user?.uid || user?.id,
    personal: {
      firstName: user?.firstName || user?.displayName?.split(' ')[0] || '',
      lastName: user?.lastName || user?.displayName?.split(' ').slice(1).join(' ') || '',
      fullName: user?.employeeName || user?.displayName || user?.firstName || user?.email?.split('@')[0] || '',
      email: user?.email || '',
      phone: user?.phone || '',
      photo: user?.photoURL || user?.photo || user?.personal?.photo || ''
    },
    job: {
      employeeCode: user?.employeeCode || user?.uid || '',
      department: user?.department || '',
      designation: user?.designation || user?.role || '',
      joiningDate: user?.joiningDate || ''
    },
    status: 'Active',
    isActive: true
  };

  const isBasicProfile = !employee;
  const profilePhoto = displayEmployee.personal?.photo || displayEmployee.photoURL || displayEmployee.photo || user?.photoURL || user?.photo || '';

  return (
    <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 profile-main-content">
        <Navbar />
        <Container fluid className="py-3 py-md-4 px-2 px-md-4 profile-content">
          <h2 className="fw-bold text-dark mb-3 mb-md-4 fs-4 fs-md-2">My Profile</h2>

          {/* Show alert if using basic profile (no employee record) */}
          {isBasicProfile && (
            <Alert variant="info" className="mb-3">
              <small>Your full employee profile is not set up yet. Contact HR to complete your profile.</small>
            </Alert>
          )}

          {/* Profile Header */}
          <Card className="mb-3 mb-md-4 shadow-sm border-0 rounded-4 overflow-hidden">
            <Card.Body className="p-3 p-md-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
              <Row className="align-items-center g-3">
                <Col xs={12} md={2} className="text-center">
                  {profilePhoto ? (
                    <img
                      src={profilePhoto}
                      alt={displayEmployee.personal?.fullName}
                      className="rounded-circle shadow"
                      style={{ width: '100px', height: '100px', objectFit: 'cover', border: '3px solid #fff' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mx-auto shadow"
                      style={{ width: '100px', height: '100px', background: '#fff', color: '#667eea', fontSize: '2.5rem', fontWeight: 'bold', border: '3px solid #fff' }}
                    >
                      {displayEmployee.personal?.firstName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase()}{displayEmployee.personal?.lastName?.charAt(0) || ''}
                    </div>
                  )}
                </Col>
                <Col xs={12} md={7} className="text-center text-md-start">
                  <h2 className="mb-1 fs-4 fs-md-2 fw-bold">{displayEmployee.personal?.fullName || displayEmployee.personal?.email || user?.email}</h2>
                  <h6 className="mb-2 opacity-90 fs-6">
                    {displayEmployee.job?.designation || user?.role?.toUpperCase() || 'User'}
                    {displayEmployee.job?.department && ` - ${displayEmployee.job.department}`}
                  </h6>
                  <div className="mb-2 small opacity-90 d-flex flex-wrap justify-content-center justify-content-md-start gap-2 gap-md-3">
                    <span><strong>Employee ID:</strong> {displayEmployee.job?.employeeCode || displayEmployee.id || 'N/A'}</span>
                    <span className="d-none d-md-inline">|</span>
                    <span className="text-break"><strong>Email:</strong> {displayEmployee.personal?.email || user?.email}</span>
                  </div>
                  <div className="d-flex flex-wrap justify-content-center justify-content-md-start gap-1">
                    {getStatusBadge(displayEmployee.status || 'Active')}
                    {user?.role && (
                      <Badge bg="primary" className="ms-1 text-capitalize">{user.role}</Badge>
                    )}
                  </div>
                </Col>
                <Col xs={12} md={3} className="text-center text-md-end mt-2 mt-md-0 d-flex justify-content-center justify-content-md-end gap-2">
                  <Button variant="light" size="sm" className="fw-bold shadow-sm" onClick={handlePrint}>
                    <FaPrint className="me-1" /> Print
                  </Button>
                  <Button variant="light" size="sm" className="fw-bold shadow-sm" onClick={handleDownloadPDF}>
                    <FaDownload className="me-1" /> PDF
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tabs */}
          <Card className="shadow-sm border-0 rounded-4">
            <Card.Body className="p-2 p-md-3">
              <div className="profile-page-tabs mb-3">
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                  <Tab eventKey="overview" title="Overview">
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <PersonalDetailsSection employee={displayEmployee} onPreview={handlePreview} />
                      </Col>
                      <Col xs={12} md={6}>
                        <JobDetailsSection employee={displayEmployee} userRole={user?.role} />
                      </Col>
                    </Row>
                  </Tab>

                  <Tab eventKey="attendance" title="Attendance">
                    <div className="p-0 p-md-2 pt-2 pt-md-3">
                      <AttendanceCalendarView
                        initialEmployeeId={displayEmployee.job?.employeeCode || displayEmployee.id}
                        hideEmployeeSelector={true}
                        isDashboard={true}
                        showDashboardStats={true}
                      />
                    </div>
                  </Tab>

                  <Tab eventKey="leaves" title="Leaves">
                    <LeaveBalanceSection employeeId={displayEmployee.id} />
                  </Tab>

                  <Tab eventKey="documents" title="Documents">
                    <DocumentsSection employee={displayEmployee} />
                  </Tab>

                  <Tab eventKey="growth" title="Career Growth & Journey">
                    <CareerGrowthSection employee={displayEmployee} onRefresh={fetchMyProfile} />
                  </Tab>
                </Tabs>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Document Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>{previewTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 text-center bg-light" style={{ minHeight: '60vh' }}>
          {previewUrl && (previewUrl.includes('pdf') || previewUrl.startsWith('data:application/pdf')) ? (
            <iframe
              src={previewUrl}
              title="Document Preview"
              style={{ width: '100%', height: '65vh', border: 'none' }}
            />
          ) : (
            <img
              src={previewUrl}
              alt="Document Preview"
              style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', padding: '16px' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowPreview(false)}>Close</Button>
          <a href={previewUrl} download={previewTitle} className="btn btn-primary btn-sm">
            <FaDownload className="me-1" /> Download
          </a>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MyProfilePage;
